import { useCallback, useEffect, useState } from 'react';
import { getSalonAdminApiBaseUrl } from '../../salon-admin/utils/apiConfig';
import {
  franchiseAuthHeaders,
  hasFranchiseFlowAuth,
  resolveFranchiseScopeIds,
} from '../../shared/franchiseFlowAuth';
import {
  resolveFaceOrganizationId,
  resolveFaceVisitOrganizationId,
} from '../utils/faceIdentityApi';

function adminBase() {
  return (getSalonAdminApiBaseUrl() || '').replace(/\/$/, '');
}

const FALLBACK_SERVICES = [
  { id: 'svc_hair', name: 'Hair Care' },
  { id: 'svc_skin', name: 'Skin Care' },
  { id: 'svc_nail', name: 'Nail services' },
];

async function fetchFranchisePriceCatalog(gender) {
  const base = adminBase();
  if (!base) throw new Error('Admin API unavailable');
  const headers = franchiseAuthHeaders();
  const qs = new URLSearchParams({ _: String(Date.now()) });
  if (gender) qs.set('gender', String(gender).toLowerCase());
  const scope = resolveFranchiseScopeIds();
  if (scope.franchiseId) qs.set('franchise_id', scope.franchiseId);
  if (scope.organizationId) qs.set('organization_id', scope.organizationId);
  const url = `${base}/franchise-price-services/catalog?${qs.toString()}`;
  const res = await fetch(url, { cache: 'no-store', credentials: 'include', headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to load franchise price list');
  const services = Array.isArray(data.services) ? data.services : [];
  return services.map((svc) => ({
    id: svc.id,
    name: svc.name,
    category: svc.category || '',
    price: svc.price || '',
    price_options: Array.isArray(svc.price_options) ? svc.price_options : [],
    description: svc.description || '',
    gender: svc.gender || 'unisex',
    service_type: svc.service_type || 'other',
    conditions: Array.isArray(svc.conditions) ? svc.conditions : [],
    net_price: svc.net_price,
    sale_price: svc.sale_price,
    discount: svc.discount,
  }));
}

async function fetchRegisterServicesCatalog() {
  const base = adminBase();
  if (!base) throw new Error('Admin API unavailable');
  const org = resolveFaceOrganizationId() || '';
  const headers = franchiseAuthHeaders();
  const url =
    `${base}/register-services/catalog` +
    `?organization_id=${encodeURIComponent(org)}` +
    `&_=${Date.now()}`;
  const res = await fetch(url, { cache: 'no-store', credentials: 'include', headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to load services');
  return Array.isArray(data.services) ? data.services : [];
}

export function normalizeGuestGender(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (['male', 'm', 'man', 'men'].includes(raw)) return 'male';
  if (['female', 'f', 'woman', 'women'].includes(raw)) return 'female';
  return '';
}

/** Keep rows for this guest; unisex / unknown gender rows match any guest. */
export function filterServicesForGuestGender(services, guestGender) {
  const g = normalizeGuestGender(guestGender);
  if (!g) return services || [];
  return (services || []).filter((svc) => {
    const row = normalizeGuestGender(svc?.gender || '');
    if (!row || row === 'unisex') return true;
    return row === g;
  });
}

async function fetchCatalogServices(gender, { requireGender = false } = {}) {
  const g = normalizeGuestGender(gender);
  if (requireGender && !g) return [];
  try {
    const franchiseList = await fetchFranchisePriceCatalog(g || undefined);
    if (franchiseList.length) {
      return g ? filterServicesForGuestGender(franchiseList, g) : franchiseList;
    }
    if (hasFranchiseFlowAuth()) {
      return [];
    }
  } catch (err) {
    if (hasFranchiseFlowAuth()) {
      throw err;
    }
  }
  const registerList = await fetchRegisterServicesCatalog();
  return g ? filterServicesForGuestGender(registerList, g) : registerList;
}

export function useRegisterServicesCatalog(guestGender, options = {}) {
  const { requireGender = false } = options;
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const gender = normalizeGuestGender(guestGender);

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    if (requireGender && !gender) {
      setServices([]);
      setError('');
      setLoading(false);
      return;
    }
    try {
      const list = await fetchCatalogServices(gender, { requireGender });
      setServices(list);
    } catch (err) {
      setError(err.message || 'Failed to load services');
      setServices(requireGender ? [] : FALLBACK_SERVICES);
    } finally {
      setLoading(false);
    }
  }, [gender, requireGender]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      if (requireGender && !gender) {
        if (!cancelled) {
          setServices([]);
          setError('');
          setLoading(false);
        }
        return;
      }
      try {
        const list = await fetchCatalogServices(gender, { requireGender });
        if (!cancelled) setServices(list);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load services');
          setServices(requireGender ? [] : FALLBACK_SERVICES);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gender, requireGender]);

  return { services, loading, error, reload };
}

export async function recordSelectedServices({
  faceUserId,
  selectedServices,
  source = 'register',
  organizationId,
  visitId,
}) {
  const base = adminBase();
  if (!base || !faceUserId) return null;

  const headers = franchiseAuthHeaders({ 'Content-Type': 'application/json' });
  const res = await fetch(`${base}/register-services/history`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({
      face_user_id: faceUserId,
      organization_id:
        organizationId ||
        resolveFaceVisitOrganizationId() ||
        resolveFaceOrganizationId(),
      selected_services: selectedServices,
      source,
      visit_id: visitId,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to save services');
  return data;
}

function baseIdOf(item) {
  if (!item) return '';
  if (item.base_service_id != null && String(item.base_service_id).trim()) {
    return String(item.base_service_id).trim();
  }
  const id = String(item.id || item.service_id || '').trim();
  if (id.includes('::')) return id.split('::')[0];
  return id || String(item.name || '').trim();
}

/** One selected line per catalog service; dual-price variants replace by base id. */
export function toggleServiceSelection(current, service) {
  const list = Array.isArray(current) ? [...current] : [];
  const baseId = baseIdOf(service);
  const exactId = String(service?.id || service?.name || '').trim();
  const idx = list.findIndex((item) => {
    if (exactId && String(item.id || '') === exactId) return true;
    if (baseId && baseIdOf(item) === baseId) return true;
    return false;
  });
  if (idx >= 0) {
    // Same variant (or same base) already selected → remove
    const existing = list[idx];
    if (
      exactId &&
      String(existing.id || '') === exactId
    ) {
      list.splice(idx, 1);
      return list;
    }
    // Different option for same base → replace
    if (baseId && baseIdOf(existing) === baseId && String(existing.id || '') !== exactId) {
      list.splice(idx, 1, buildSelectionEntry(service));
      return list;
    }
    list.splice(idx, 1);
    return list;
  }
  return [...list, buildSelectionEntry(service)];
}

function buildSelectionEntry(service) {
  const next = {
    id: service.id,
    name: service.name,
  };
  if (service.base_service_id) next.base_service_id = service.base_service_id;
  if (service.price != null && service.price !== '') next.price = String(service.price);
  if (service.amount != null && Number.isFinite(Number(service.amount))) {
    next.amount = Number(service.amount);
  }
  if (service.option_key) next.option_key = service.option_key;
  if (service.option_label) next.option_label = service.option_label;
  if (service.category) next.category = service.category;
  if (service.description) next.description = service.description;
  if (service.gender) next.gender = service.gender;
  return next;
}

export function useServiceSelection(initial = []) {
  const [selected, setSelected] = useState(initial);
  const toggle = useCallback((service) => {
    setSelected((prev) => toggleServiceSelection(prev, service));
  }, []);
  const validate = useCallback(() => {
    if (!selected.length) return 'Select at least one service to continue';
    return '';
  }, [selected]);
  return { selected, setSelected, toggle, validate };
}
